import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { add, differenceInDays, sub, toDate } from "date-fns";
import { Dispatch } from "react";
import {
  AirportOption,
  DirectionTypes,
  RMFlightWrapper,
  FlightTypeSelect,
  RMDestination,
  SearchFlightsForm,
  TimeSelect,
  RMFlightQueryResponse,
  RMLocation,
} from "../../types";
import { RootState } from "../../app/store";
import { fetchConfig } from "../config/ConfigActions";
import {
  fetchDestinationsResults,
  fetchOutboundConnections,
  fetchOutboundDirects,
  fetchResults,
  fetchReturnConnections,
  fetchReturnDirects,
} from "./SearchFlightsActions";
import { generateFlightKey } from "./utils";

import queryString from "query-string";
import { getTranslatedLabel } from "../../utils";

export enum FlightTimeSortTypes {
  Departure = "departureMinuteOfDay",
  Arrival = "arrivalMinuteOfDay",
  Duration = "totalTravelTime",
}

export interface FlightResultsFilters {
  airline: string;
  via: string;
  stops: number;
  sortBy: FlightTimeSortTypes;
  departureTime: any;
  arrivalTime: any;
}
export interface FlightResults {
  direction: DirectionTypes;
  type: string;
  loading: boolean;
  hasResults: boolean;
  filter: FlightResultsFilters;
  currentSelection: FlightResultContent;
  flightSelected: RMFlightWrapper;
  checkedFlights: object;
  outboundDirect: FlightResultContent;
  returnDirect: FlightResultContent;
  outboundConnection: FlightResultContent;
  returnConnection: FlightResultContent;
}
export interface DestinationResults {
  loading: boolean;
  hasResults: boolean;
  results: RMDestination[];
}

export interface SearchFlightsState {
  defaultValues: any;
  form: SearchFlightsForm;
  startAirport: string;
  startAirports: AirportOption[];
  formValidity: any;
  defaultFilter: FlightResultsFilters;
  results: FlightResults;
  destinationsResults: DestinationResults;
  oneSubTab: boolean;
  isAirline: boolean;
  display: "none" | "flights" | "destinations";
}
export type FlightResultContent = {
  flights: RMFlightWrapper[];
  filtered?: RMFlightWrapper[];
  via: string[];
  dateMin: Date;
  dateMax: Date;
  loading: boolean;
  error: boolean;
};

const today = new Date();
const todayUtc = Date.UTC(
  today.getUTCFullYear(),
  today.getUTCMonth(),
  today.getUTCDate()
);

const initialState: Partial<SearchFlightsState> = {
  defaultValues: {
    departingDateFrom: todayUtc,
    departingDate: todayUtc,
    departingDateTo: todayUtc,
    departingTime: "00:01",
    departingTimeSelect: TimeSelect.AnyTime,
    returningDateFrom: todayUtc,
    returningDate: todayUtc,
    returningDateTo: todayUtc,
    returningTime: "00:01",
    returningTimeSelect: TimeSelect.AnyTime,
    originAirport: null,
    originAirportInput: "",
    airport: null,
    flightType: FlightTypeSelect.NonStop,
  },
  startAirport: null,
  startAirports: [],
  form: {
    direction: "from",
    originAirport: null,
    destinationAirport: null,
    destinationAnywhere: false,
    viaAirport: null,
    departingDateFrom: todayUtc,
    departingDate: todayUtc,
    departingDateTo: todayUtc,
    departingTime: "00:01",
    departingTimeSelect: TimeSelect.AnyTime,
    returningDateFrom: todayUtc,
    returningDate: todayUtc,
    returningDateTo: todayUtc,
    returningTime: "00:01",
    returningTimeSelect: TimeSelect.AnyTime,
    nonStopFlights: false,
    dateRange: false,
    dateRangeWeekly: false,
    roundTrip: true,
    codeShares: true,
    disabled: false,
    includeCodeShare: true,
    flightType: FlightTypeSelect.NonStop,
  },
  formValidity: {
    originAirport: true,
    destinationAirport: true,
    departingDateFilled: true,
    returningDateFilled: true,
    departingDateBeforeReturningDate: true,
    roundTripDateRangeRestriction: true,
  },
  defaultFilter: {
    airline: "",
    via: "",
    stops: -1,
    sortBy: FlightTimeSortTypes.Departure,
    departureTime: { start: 0, end: 1440 },
    arrivalTime: { start: 0, end: 1440 },
  },
  results: {
    direction: DirectionTypes.Outbound,
    type: "directAirport",
    loading: false,
    hasResults: false,
    filter: {
      airline: "",
      via: "",
      stops: -1,
      sortBy: FlightTimeSortTypes.Departure,
      departureTime: { start: 0, end: 1440 },
      arrivalTime: { start: 0, end: 1440 },
    },
    currentSelection: null,
    flightSelected: null,
    checkedFlights: {},
    outboundDirect: {
      flights: [],
      via: [],
      dateMin: null,
      dateMax: null,
      loading: false,
      error: false,
    },
    returnDirect: {
      flights: [],
      via: [],
      dateMin: null,
      dateMax: null,
      loading: false,
      error: false,
    },
    outboundConnection: {
      flights: [],
      via: [],
      dateMin: null,
      dateMax: null,
      loading: false,
      error: false,
    },
    returnConnection: {
      flights: [],
      via: [],
      dateMin: null,
      dateMax: null,
      loading: false,
      error: false,
    },
  },
  destinationsResults: {
    loading: false,
    hasResults: false,
    results: [],
  },
  oneSubTab: false,
  isAirline: false,
  display: "none",
};

function sundayToSunday(date: number) {
  const dateFrom = new Date(date);
  const dateTo = new Date(date);
  while (dateFrom.getDay() !== 0) {
    dateFrom.setDate(dateFrom.getDate() - 1);
  }
  while (dateTo.getDay() !== 6) {
    dateTo.setDate(dateTo.getDate() + 1);
  }
  return {
    dateFrom: dateFrom,
    dateTo: dateTo,
  };
}

export const searchFlightsSlice = createSlice({
  name: "searchFlights",
  initialState: initialState as SearchFlightsState,
  reducers: {
    updateForm: (state, action) => {
      const previousDirection = state.form.direction;
      state.form = { ...state.form, ...action.payload };
      state.formValidity.departingDateBeforeReturningDate =
        state.form.departingDate !== null &&
        state.form.returningDate !== null &&
        state.form.roundTrip
          ? state.form.departingDate <= state.form.returningDate
          : true;
      if (previousDirection === "via") {
        state.form.originAirport = state.form.viaAirport;
        state.form.viaAirport = null;
        state.form.destinationAirport = null;
      }
    },
    switchAirports: (state) => {
      const origin = state.form.originAirport;
      const dest = state.form.destinationAirport;
      state.form.originAirport = dest;
      state.form.destinationAirport = origin;
      state.form.destinationAnywhere = false;
    },
    updateFormDirection: (state, action) => {
      const previousValue = state.form.direction;
      state.form.direction = action.payload;
      if (previousValue !== state.form.direction) {
        const origin = state.form.originAirport;
        const dest = state.form.destinationAirport;
        if (state.form.direction === "to" || state.form.direction === "from") {
          if (previousValue === "via") {
            if (state.defaultValues.lockOrigin) {
              state.form.originAirport = state.form.viaAirport;
              state.form.destinationAirport = null;
            }
            if (state.defaultValues.lockDestination) {
              state.form.destinationAirport = state.form.viaAirport;
              state.form.originAirport = null;
            }
          } else {
            state.form.originAirport = dest;
            state.form.destinationAirport = origin;
          }
          state.form.viaAirport = null;
        } else {
          state.form.viaAirport =
            state.form.originAirport || state.form.destinationAirport;
          state.form.originAirport = null;
          state.form.destinationAirport = null;
        }
      }
    },
    setInitialOriginAirport: (state, action: PayloadAction<RMLocation>) => {
      state.defaultValues.originAirport = action.payload;
      state.defaultValues.originAirportInput =
        (action.payload && getTranslatedLabel(action.payload.port)) || "";
    },
    setInitialDestinationAirport: (
      state,
      action: PayloadAction<RMLocation>
    ) => {
      state.defaultValues.destinationAirport = action.payload;
      state.defaultValues.destinationAirportInput =
        (action.payload && getTranslatedLabel(action.payload.port)) || "";
    },
    updateOriginAirport: (state, action: PayloadAction<RMLocation>) => {
      state.form.originAirport = action.payload;
    },
    updateAirport: (state, action: PayloadAction<RMLocation>) => {
      if (
        state.form.originAirport &&
        !state.form.destinationAirport?.readOnly
      ) {
        state.form.destinationAirport = action.payload;
      } else {
        state.form.originAirport = action.payload;
      }
    },
    updateDestinationAirport: (state, action: PayloadAction<RMLocation>) => {
      state.form.destinationAirport = action.payload;
      state.formValidity.destinationAirport = action.payload !== null;
    },
    updateViaAirport: (state, action: PayloadAction<RMLocation>) => {
      state.form.viaAirport = action.payload;
      state.formValidity.viaAirport = action.payload !== null;
    },
    updateDepartingDate: (state, action: PayloadAction<number>) => {
      if (action.payload >= state.form.returningDate) {
        state.form.returningDate = add(action.payload, {
          days: differenceInDays(
            state.form.returningDate,
            state.form.departingDate
          ),
        }).getTime();
      }
      state.form.departingDate = action.payload;
      calculateDates(state.form);
      state.formValidity.departingDateFilled = action.payload !== null;
      state.formValidity.departingDateBeforeReturningDate =
        state.form.departingDate !== null &&
        state.form.returningDate !== null &&
        state.form.roundTrip
          ? state.form.departingDate <= state.form.returningDate
          : true;
    },
    updateReturningDate: (state, action: PayloadAction<number>) => {
      state.form.returningDate = action.payload;
      calculateDates(state.form);
      state.formValidity.returningDateFilled = action.payload !== null;
      state.formValidity.departingDateBeforeReturningDate =
        state.form.departingDate !== null &&
        state.form.returningDate !== null &&
        state.form.roundTrip
          ? state.form.departingDate <= state.form.returningDate
          : true;
    },
    updateDates: (state, action) => {
      state.form.departingDate = action.payload.departingDate;
      state.form.returningDate = action.payload.returningDate;
      calculateDates(state.form);
    },
    updateResultsFilters: (state, action) => {
      state.results.filter = { ...state.results.filter, ...action.payload };
      state.results.currentSelection = filterSelection(
        state.results.currentSelection,
        state
      );
    },
    updateFormValidity: (state, action) => {
      state.formValidity = { ...state.formValidity, ...action.payload };
    },
    resetResults: (state, action: PayloadAction<string>) => {
      if (action.payload === "flights") {
        state.results = initialState.results;
        if (state.isAirline) {
          state.form = {
            ...initialState.form,
            ...state.defaultValues,
          };
        }
        if (state.form.destinationAnywhere) {
          state.display = "destinations";
          state.form.destinationAirport = null;
        } else {
          state.form.disabled = false;
          state.display = "none";
        }
      } else if (action.payload === "destinations") {
        state.destinationsResults = initialState.destinationsResults;
        state.form.destinationAirport = null;
        state.form.disabled = false;
        state.display = "none";
      }
    },
    resetForm: (state) => {
      const previousValue = state.form.direction;
      const originAirport = state.form.viaAirport;
      state.form = {
        ...initialState.form,
        ...state.defaultValues,
      };
      if (state.startAirport) {
        state.startAirport = state.defaultValues.airport;
      }
      state.formValidity = initialState.formValidity;
      state.results = initialState.results;
      state.form.disabled = false;
      if (previousValue === "via") {
        state.form.originAirport = originAirport;
        searchFlightsSlice.caseReducers.updateFormDirection(state, {
          payload: previousValue,
          type: null,
        });
      }
    },
    updateDirectionResults: (state, action: PayloadAction<DirectionTypes>) => {
      state.results.direction = action.payload;
      const selection = defineCurrentSelection(state);
      state.results.currentSelection = filterSelection(selection, state);
    },
    updateResultsSelection: (state, action) => {
      state.results.type = action.payload.type;
      state.results.direction = action.payload.direction;
      const selection = defineCurrentSelection(state);
      state.results.currentSelection = filterSelection(selection, state);
      state.results.checkedFlights = {};
    },
    updateSelectedFlight: (state, action: PayloadAction<RMFlightWrapper>) => {
      state.results.flightSelected = action.payload;
      const htmlClasses = document.querySelector("html").classList;
      if (action.payload) {
        htmlClasses.add("is-clipped");
      } else {
        htmlClasses.remove("is-clipped");
      }
    },
    updateCheckedFlights: (state, action) => {
      const value = state.results.checkedFlights[action.payload.key];
      if (value) {
        state.results.checkedFlights[action.payload.key] = undefined;
      } else {
        state.results.checkedFlights[action.payload.key] =
          action.payload.flight;
      }
    },
    updateAllCheckedFlights: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        for (
          let i = 0;
          i < state.results.currentSelection.flights.length;
          i++
        ) {
          const flight = state.results.currentSelection.flights[i];
          const key = generateFlightKey(flight);
          state.results.checkedFlights[key] = flight;
        }
        state.results.checkedFlights["all"] = true;
      } else {
        state.results.checkedFlights = {};
      }
    },
    updateDateRange: (state, action: PayloadAction<boolean>) => {
      state.form.dateRange = action.payload;
      if (state.form.dateRange) {
        state.form.dateRangeWeekly = false;
      }
      calculateDates(state.form);
    },
    updateDateRangeWeekly: (state, action: PayloadAction<boolean>) => {
      state.form.dateRangeWeekly = action.payload;
      if (state.form.dateRangeWeekly) {
        state.form.dateRange = false;
      }
      calculateDates(state.form);
    },
    updateStartAirport: (state, action: PayloadAction<string>) => {
      state.startAirport = action.payload;
    },
    updateStartAirports: (state, action: PayloadAction<AirportOption[]>) => {
      state.startAirports = action.payload;
    },
  },
  extraReducers: {
    [fetchConfig.fulfilled.toString()]: (state, action) => {
      if (
        action.payload.AppComponentsCustom.Results.flightFinder.tabs.oneSubTab
      ) {
        state.oneSubTab =
          action.payload.AppComponentsCustom.Results.flightFinder.tabs.oneSubTab.display;
      }
      const inputs = action.payload.AppComponentsCustom.Inputs;
      const filter = action.payload.AppComponentsCustom.FilterAndSortMenus;
      let isAirport = action.payload.AppConfigCustom.customerType.airport;
      const parsed = queryString.parse(window.location.search);
      const airlineCode = parsed["airline"] || parsed["?airline"];
      if (airlineCode) {
        isAirport = false;
        state.isAirline = true;
      }
      const airportCode = parsed["airport"] || parsed["?airport"];
      if (airportCode) {
        if (!Array.isArray(airportCode)) {
          state.startAirport = airportCode;
        }
      }
      if (inputs.RequestDate.init === false) {
        state.form.departingDate = null;
        state.defaultValues.departingDate = null;
      } else if (inputs.RequestDate.initDayOffset) {
        const departingDate = add(todayUtc, {
          days: inputs.RequestDate.initDayOffset,
        }).getTime();
        state.form.departingDate = departingDate;
        state.defaultValues.departingDate = departingDate;
      }
      if (inputs.ReturnRequestDate.init === false) {
        state.form.returningDate = null;
        state.defaultValues.returningDate = null;
      } else if (inputs.ReturnRequestDate.initDayOffset) {
        const returningDate = add(todayUtc, {
          days: inputs.ReturnRequestDate.initDayOffset,
        }).getTime();
        state.form.returningDate = returningDate;
        state.defaultValues.returningDate = returningDate;
      }

      if (isAirport) {
        state.defaultValues.airport = action.payload.MapperCustom.originPort;
        state.defaultValues.lockOrigin = action.payload.MapperCustom.lockOrigin;
        state.defaultValues.lockDestination =
          action.payload.MapperCustom.lockDestination;
        if (
          action.payload.AppConfigCustom.customerType.airportVersion.airport !==
          null
        ) {
          state.startAirport =
            action.payload.AppConfigCustom.customerType.airportVersion.airport;
        }
        if (action.payload.AppConfigCustom.customerType.airportVersion.multi) {
          state.startAirports =
            action.payload.AppConfigCustom.customerType.airportVersion.airports.map(
              (airport) => {
                return {
                  code: airport,
                  label: airport,
                };
              }
            );
        }
      }
      if (filter.sortDefault) {
        state.defaultFilter.sortBy = filter.sortDefault;
      }
      if (inputs.NonStopFlights.value !== undefined) {
        state.form.nonStopFlights = inputs.NonStopFlights.value;
        state.defaultValues.nonStopFlights = inputs.NonStopFlights.value;
      }
      if (inputs.RoundTrip.onewayDefault !== undefined) {
        state.form.roundTrip = !inputs.RoundTrip.onewayDefault;
        state.defaultValues.roundTrip = !inputs.RoundTrip.onewayDefault;
      }
      if (inputs.IncludeCodeShare.checked !== undefined) {
        state.form.includeCodeShare = inputs.IncludeCodeShare.checked;
        state.defaultValues.includeCodeShare = inputs.IncludeCodeShare.checked;
      }
      if (inputs.DateRange.value) {
        state.form.dateRange = inputs.DateRange.value;
        state.defaultValues.dateRange = inputs.DateRange.value;
      }
      if (inputs.DateRangeWeekly.weeklyDefault) {
        state.form.dateRangeWeekly = inputs.DateRangeWeekly.weeklyDefault;
        state.defaultValues.dateRangeWeekly =
          inputs.DateRangeWeekly.weeklyDefault;
      }
      calculateDates(state.form);
    },
    [fetchDestinationsResults.pending.toString()]: (state) => {
      state.destinationsResults = {
        ...initialState.destinationsResults,
        loading: true,
      };
      state.form.disabled = true;
      state.display = "none";
    },
    [fetchDestinationsResults.fulfilled.toString()]: (state, action) => {
      state.destinationsResults.results = action.payload.results;
      state.destinationsResults.hasResults = true;
      state.destinationsResults.loading = false;
      state.display = "destinations";
    },
    [fetchResults.pending.toString()]: (state) => {
      state.results = {
        ...initialState.results,
        filter: state.defaultFilter,
        loading: true,
        outboundDirect: {
          ...initialState.results.outboundDirect,
          loading: true,
        },
        outboundConnection: {
          ...initialState.results.outboundConnection,
          loading: true,
        },
        returnDirect: { ...initialState.results.returnDirect, loading: true },
        returnConnection: {
          ...initialState.results.returnConnection,
          loading: true,
        },
      };
      state.form.disabled = true;
      state.display = "none";
    },
    [fetchResults.fulfilled.toString()]: (state, action) => {
      state.results.loading = false;

      // Check if flight type toggle is enabled and set appropriate result type
      const { enableFlightTypeToggle, visibleFlightType } = action.payload;

      if (enableFlightTypeToggle) {
        // If toggle feature is enabled, set result type based on visible flight type
        if (visibleFlightType === "connection") {
          state.results.type = "connectionAirport";
        } else if (visibleFlightType === "direct") {
          state.results.type = "directAirport";
        }
      } else {
        // Default behavior: if no direct flights but has connections, show connections
        if (
          state.results.outboundDirect.flights.length === 0 &&
          state.results.outboundConnection.flights.length > 0
        ) {
          state.results.direction = DirectionTypes.Outbound;
          state.results.type = "connectionAirport";
        }
      }

      const selection = defineCurrentSelection(state);
      state.results.currentSelection = filterSelection(selection, state);
      state.display = "flights";
    },
    [fetchResults.rejected.toString()]: (state) => {
      state.results.loading = false;
    },
    [fetchOutboundDirects.rejected.toString()]: (state) => {
      state.results.outboundDirect = {
        ...state.results.outboundDirect,
        loading: false,
        error: true,
      };
    },
    [fetchOutboundDirects.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.outboundDirect = {
        flights:
          filterResultsByTime(
            filterResults(action.payload),
            DirectionTypes.Outbound,
            state.form
          ) || [],
        via: action.payload.viaPoints || [],
        dateMin:
          action.payload.miniumResultDate &&
          toDate(action.payload.miniumResultDate),
        dateMax:
          action.payload.maxiumResultDate &&
          toDate(action.payload.maxiumResultDate),
        loading: false,
        error: false,
      };
      if (state.results.outboundDirect.flights.length > 0) {
        state.results.hasResults = true;
        state.results.type = "directAirport";
        state.results.direction = DirectionTypes.Outbound;
        const selection = defineCurrentSelection(state);
        state.results.currentSelection = filterSelection(selection, state);
      }
    },
    [fetchOutboundConnections.rejected.toString()]: (state) => {
      state.results.outboundConnection = {
        ...state.results.outboundConnection,
        loading: false,
        error: true,
      };
    },
    [fetchOutboundConnections.fulfilled.toString()]: (
      state,
      action: PayloadAction<any>
    ) => {
      state.results.outboundConnection = {
        flights:
          filterResultsByTime(
            action.payload.results,
            DirectionTypes.Outbound,
            state.form
          ) || [],
        via: action.payload.viaPoints || [],
        dateMin:
          action.payload.miniumResultDate &&
          toDate(action.payload.miniumResultDate),
        dateMax:
          action.payload.maxiumResultDate &&
          toDate(action.payload.maxiumResultDate),
        loading: false,
        error: false,
      };
      if (state.results.outboundConnection.flights.length > 0) {
        state.results.hasResults = true;
      }
    },
    [fetchReturnDirects.rejected.toString()]: (state) => {
      state.results.returnDirect = {
        ...state.results.returnDirect,
        loading: false,
        error: true,
      };
    },
    [fetchReturnDirects.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.returnDirect = {
        flights:
          filterResultsByTime(
            filterResults(action.payload),
            DirectionTypes.Return,
            state.form
          ) || [],
        via: action.payload.viaPoints || [],
        dateMin:
          action.payload.miniumResultDate &&
          toDate(action.payload.miniumResultDate),
        dateMax:
          action.payload.maxiumResultDate &&
          toDate(action.payload.maxiumResultDate),
        loading: false,
        error: false,
      };
      if (state.results.returnDirect.flights.length > 0) {
        state.results.hasResults = true;
      }
    },
    [fetchReturnConnections.rejected.toString()]: (state) => {
      state.results.returnConnection = {
        ...state.results.returnConnection,
        loading: false,
        error: true,
      };
    },
    [fetchReturnConnections.fulfilled.toString()]: (
      state,
      action: PayloadAction<any>
    ) => {
      state.results.returnConnection = {
        flights:
          filterResultsByTime(
            action.payload.results,
            DirectionTypes.Return,
            state.form
          ) || [],
        via: action.payload.viaPoints || [],
        dateMin:
          action.payload.miniumResultDate &&
          toDate(action.payload.miniumResultDate),
        dateMax:
          action.payload.maxiumResultDate &&
          toDate(action.payload.maxiumResultDate),
        loading: false,
        error: false,
      };
      if (state.results.returnConnection.flights.length > 0) {
        state.results.hasResults = true;
      }
    },
  },
});

export const {
  updateForm,
  updateFormDirection,
  updateAirport,
  setInitialOriginAirport,
  setInitialDestinationAirport,
  updateOriginAirport,
  updateDestinationAirport,
  updateViaAirport,
  updateDepartingDate,
  updateReturningDate,
  updateDates,
  updateResultsFilters,
  updateFormValidity,
  updateDateRange,
  updateDateRangeWeekly,
  resetResults,
  resetForm,
  updateDirectionResults,
  updateResultsSelection,
  updateSelectedFlight,
  updateCheckedFlights,
  updateAllCheckedFlights,
  switchAirports,
  updateStartAirport,
  updateStartAirports,
} = searchFlightsSlice.actions;

function calculateDates(form: SearchFlightsForm) {
  if (form.dateRange) {
    // Dates +/- 3 days
    form.departingDateFrom = sub(form.departingDate, {
      days: 3,
    }).getTime();
    form.departingDateTo = add(form.departingDate, {
      days: 3,
    }).getTime();
    form.returningDateFrom = sub(form.returningDate, {
      days: 3,
    }).getTime();
    form.returningDateTo = add(form.returningDate, {
      days: 3,
    }).getTime();
  } else if (form.dateRangeWeekly) {
    // Dates from Sunday to Saturday
    const departingRange = sundayToSunday(form.departingDate);
    form.departingDateFrom = departingRange.dateFrom.getTime();
    form.departingDateTo = departingRange.dateTo.getTime();
    const returningRange = sundayToSunday(form.returningDate);
    form.returningDateFrom = returningRange.dateFrom.getTime();
    form.returningDateTo = returningRange.dateTo.getTime();
  } else {
    form.departingDateFrom = form.departingDate;
    form.departingDateTo = form.departingDate;
    form.returningDateFrom = form.returningDate;
    form.returningDateTo = form.returningDate;
  }
}

function filterResultsByTime(
  results: RMFlightWrapper[],
  type: DirectionTypes,
  form: SearchFlightsForm
): RMFlightWrapper[] {
  // We ask te API results from a certain time, but there is no upper bound there, so we need to filter that ourselves.
  if (
    type === DirectionTypes.Outbound &&
    form.departingTimeSelect !== TimeSelect.AnyTime
  ) {
    return filterResultsByTimeHelper(results, form.departingTimeSelect);
  }
  if (
    type === DirectionTypes.Return &&
    form.returningTimeSelect !== TimeSelect.AnyTime
  ) {
    return filterResultsByTimeHelper(results, form.returningTimeSelect);
  }
  return results;
}

function filterResultsByTimeHelper(
  results: RMFlightWrapper[],
  timeSelect: TimeSelect
): RMFlightWrapper[] {
  if (timeSelect === TimeSelect.Morning) {
    // 12:01 AM => 1:00 PM
    return results.filter((result) => {
      const date = new Date(result.departureDate);
      return (
        date.getUTCHours() < 13 ||
        (date.getUTCHours() === 13 && date.getUTCMinutes() === 0)
      );
    });
  } else if (timeSelect === TimeSelect.Afternoon) {
    // 11:00 AM => 6:00 PM
    return results.filter((result) => {
      const date = new Date(result.departureDate);
      return (
        date.getUTCHours() >= 11 &&
        (date.getUTCHours() < 18 ||
          (date.getUTCHours() === 18 && date.getUTCMinutes() === 0))
      );
    });
  } else if (timeSelect === TimeSelect.Evening) {
    // 5:00 PM => 12:00 AM
    return results.filter((result) => {
      const date = new Date(result.departureDate);
      return (
        date.getUTCHours() >= 17 ||
        (date.getUTCHours() === 0 && date.getUTCMinutes() === 0)
      );
    });
  } else {
    return results;
  }
}

function filterResults(payload: RMFlightQueryResponse): RMFlightWrapper[] {
  return payload.results.filter((result) => {
    if (payload.requestDetail.nonStopDirect) {
      return result.stops === "0";
    } else {
      return true;
    }
  });
}

function defineCurrentSelection(
  state: SearchFlightsState
): FlightResultContent {
  const direction = state.results.direction;
  const type = state.results.type;
  const result: FlightResultContent = {
    flights: [],
    via: [],
    dateMin: undefined,
    dateMax: undefined,
    loading: false,
    error: false,
  };
  if (direction === DirectionTypes.Outbound) {
    if (state.oneSubTab) {
      result.flights = state.results.outboundDirect.flights.concat(
        state.results.outboundConnection.flights
      );
      result.via = state.results.outboundDirect.via.concat(
        state.results.outboundConnection.via
      );
      result.dateMax =
        state.results.outboundConnection.dateMax ||
        state.results.outboundDirect.dateMax;
      result.dateMin =
        state.results.outboundConnection.dateMin ||
        state.results.outboundDirect.dateMin;
      return result;
    } else if (type === "directAirport") {
      return state.results.outboundDirect;
    } else if (type === "connectionAirport") {
      return state.results.outboundConnection;
    }
  } else if (direction === DirectionTypes.Return) {
    if (state.oneSubTab) {
      result.flights = state.results.returnDirect.flights.concat(
        state.results.returnConnection.flights
      );
      result.via = state.results.returnDirect.via.concat(
        state.results.returnConnection.via
      );
      result.dateMax =
        state.results.returnConnection.dateMax ||
        state.results.returnDirect.dateMax;
      result.dateMin =
        state.results.returnConnection.dateMin ||
        state.results.returnDirect.dateMin;
      return result;
    } else if (type === "directAirport") {
      return state.results.returnDirect;
    } else if (type === "connectionAirport") {
      return state.results.returnConnection;
    }
  }
  return null;
}

function filterSelection(
  selection: FlightResultContent,
  state: SearchFlightsState
): FlightResultContent {
  const filters = state.results.filter;
  selection.filtered = selection.flights
    .filter((v) =>
      filters.airline === "" ? true : v.operatedBy === filters.airline
    )
    .filter((v) =>
      state.results.type === "directAirport" || filters.via === ""
        ? true
        : v.routeKey.includes(filters.via)
    )
    .filter(
      (v) =>
        v.departureMinuteOfDay >= filters.departureTime.start &&
        v.departureMinuteOfDay <= filters.departureTime.end &&
        v.arrivalMinuteOfDay >= filters.arrivalTime.start &&
        v.arrivalMinuteOfDay <= filters.arrivalTime.end
    )
    .filter((v) =>
      filters.stops === -1 ? true : parseInt(v.stops) === filters.stops
    );
  sortResults(selection.filtered, state.results.filter.sortBy);
  return selection;
}

function sortResults(results: RMFlightWrapper[], sortBy: FlightTimeSortTypes) {
  if (sortBy === FlightTimeSortTypes.Departure) {
    results.sort((a, b) => a.departureMinuteOfDay - b.departureMinuteOfDay);
  } else if (sortBy === FlightTimeSortTypes.Arrival) {
    results.sort((a, b) => a.arrivalMinuteOfDay - b.arrivalMinuteOfDay);
  } else if (sortBy === FlightTimeSortTypes.Duration) {
    results.sort((a, b) => a.totalTravelTime - b.totalTravelTime);
  }
}

export const changeForm = (form) => (dispatch) => {
  dispatch(updateForm(form));
};

export const changeFormDirection = (direction) => (dispatch) => {
  dispatch(updateFormDirection(direction));
};

export const airportsSwitch = () => (dispatch) => {
  dispatch(switchAirports());
};

export const changeAirport = (airport: RMLocation) => (dispatch) => {
  dispatch(updateAirport(airport));
};

export const changeInitialOriginAirport =
  (airport: RMLocation) => (dispatch) => {
    dispatch(setInitialOriginAirport(airport));
  };

export const changeOriginAirport = (airport: RMLocation) => (dispatch) => {
  dispatch(updateOriginAirport(airport));
};

export const changeDestinationAirport = (airport: RMLocation) => (dispatch) => {
  dispatch(updateDestinationAirport(airport));
};

export const changeInitialDestinationAirport =
  (airport: RMLocation) => (dispatch) => {
    dispatch(setInitialDestinationAirport(airport));
  };

export const changeViaAirport = (airport: RMLocation) => (dispatch) => {
  dispatch(updateViaAirport(airport));
};

export const changeDepartingDate =
  (date: number) => (dispatch: Dispatch<any>) => {
    dispatch(updateDepartingDate(date));
  };

export const changeReturningDate =
  (date: number) => (dispatch: Dispatch<any>) => {
    dispatch(updateReturningDate(date));
  };

export const changeDates = (dates) => (dispatch) => {
  dispatch(updateDates(dates));
};

export const changeFormValidity = (formValidity) => (dispatch) => {
  dispatch(updateFormValidity(formValidity));
};

export const changeDateRange = (dateRange) => (dispatch) => {
  dispatch(updateDateRange(dateRange));
};

export const changeDateRangeWeekly = (dateRangeWeekly) => (dispatch) => {
  dispatch(updateDateRangeWeekly(dateRangeWeekly));
};

export const resetFlightsResults =
  (type: "none" | "flights" | "destinations") => (dispatch) => {
    dispatch(resetResults(type));
  };

export const resetFlightsForm = () => (dispatch) => {
  dispatch(resetForm());
};

export const changeDirection = (direction: DirectionTypes) => (dispatch) => {
  dispatch(updateDirectionResults(direction));
};

export const changeResultsSelection =
  (type: string, direction: DirectionTypes) => (dispatch) => {
    dispatch(updateResultsSelection({ type, direction }));
  };

export const changeResultsFilters = (filters) => (dispatch) => {
  dispatch(updateResultsFilters(filters));
};

export const changeSelectedFlight = (flight: RMFlightWrapper) => (dispatch) => {
  dispatch(updateSelectedFlight(flight));
};

export const changeCheckedFlights = (flight) => (dispatch) => {
  dispatch(updateCheckedFlights(flight));
};

export const changeAllCheckedFlights = (selectAll: boolean) => (dispatch) => {
  dispatch(updateAllCheckedFlights(selectAll));
};

export const changeStartAirport = (startAirport: string) => (dispatch) => {
  dispatch(updateStartAirport(startAirport));
};

export const changeStartAirports =
  (startAirports: AirportOption[]) => (dispatch) => {
    dispatch(updateStartAirports(startAirports));
  };

export const selectDestinationAirport = (state: RootState) =>
  state.searchFlights.form.destinationAirport;

export const selectSearchFlightsForm = (state: RootState) =>
  state.searchFlights.form;

export const selectChosenFlightsResults = (state: RootState) =>
  state.searchFlights.results.currentSelection;

export const selectFormValidity = (state: RootState) =>
  state.searchFlights.formValidity;

export const selectSelectedFlight = (state: RootState) =>
  state.searchFlights.results.flightSelected;

export const selectResultsFilters = (state: RootState) =>
  state.searchFlights.results.filter;

export const selectCheckedFlights = (state: RootState) =>
  state.searchFlights.results.checkedFlights;

export default searchFlightsSlice.reducer;
